import React, {useEffect, useState, useCallback, useRef} from 'react';
import {Icon} from '@iconify/react/dist/iconify.js';
import {fetchSmsBalance, purchaseSmsCredits} from '../utils/requests/smsCredits';
import {fetchPaymentMethods, addPaymentMethod} from '../utils/requests/payments';
import {useAtom} from 'jotai';
import {settingsAtom} from '../utils/atoms';
import {useLocation, useNavigate} from 'react-router-dom';

const TAX_RATE = 0.18;
const withTax = (price) => Math.ceil(price * (1 + TAX_RATE) * 100) / 100;

const PACKAGES = [
    {id: 1, amount: 500, label: '500 הודעות', price: 75, pricePerSms: '0.15'},
    {id: 2, amount: 1000, label: '1,000 הודעות', price: 130, pricePerSms: '0.13', popular: true},
    {id: 3, amount: 2500, label: '2,500 הודעות', price: 275, pricePerSms: '0.11'},
    {id: 4, amount: 5000, label: '5,000 הודעות', price: 500, pricePerSms: '0.10'},
];

const SmsCreditsPurchaseLayer = () => {
    const [balance, setBalance] = useState(null);
    const [cards, setCards] = useState([]);
    const [loadingBalance, setLoadingBalance] = useState(true);
    const [loadingCards, setLoadingCards] = useState(true);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [purchasing, setPurchasing] = useState(false);
    const [purchaseResult, setPurchaseResult] = useState(null);
    const [error, setError] = useState(null);

    // Add card modal state
    const [showAddCardModal, setShowAddCardModal] = useState(false);
    const [addCardLoading, setAddCardLoading] = useState(false);
    const [cardFormData, setCardFormData] = useState({name: '', phoneNumber: ''});
    const [settings] = useAtom(settingsAtom);
    const location = useLocation();
    const navigate = useNavigate();
    const [hasHandledRedirect, setHasHandledRedirect] = useState(false);

    const loadBalance = useCallback(async () => {
        try {
            const data = await fetchSmsBalance();
            setBalance(data);
        } catch (err) {
            console.error('Failed to load SMS balance:', err);
        } finally {
            setLoadingBalance(false);
        }
    }, []);

    const loadCards = useCallback(async () => {
        try {
            const data = await fetchPaymentMethods();
            setCards(data);
            // Auto-select default card
            const defaultCard = data.find(c => c.isDefault);
            if (defaultCard) {
                setSelectedCardId(defaultCard.id);
            } else if (data.length > 0) {
                setSelectedCardId(data[0].id);
            }
        } catch (err) {
            console.error('Failed to load cards:', err);
        } finally {
            setLoadingCards(false);
        }
    }, []);

    useEffect(() => {
        loadBalance();
        loadCards();
    }, [loadBalance, loadCards]);

    // Handle redirect from add-card flow
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const success = urlParams.get('success');

        if (success !== null && !hasHandledRedirect) {
            setHasHandledRedirect(true);
            loadCards();
            navigate('/sms-credits', {replace: true});
        }
    }, [location.search, navigate, loadCards, hasHandledRedirect]);

    useEffect(() => {
        setHasHandledRedirect(false);
    }, [location.pathname]);

    // Pre-fill add-card form
    useEffect(() => {
        if (showAddCardModal && settings?.teacher) {
            const teacherName = `${settings.teacher.firstName || ''} ${settings.teacher.lastName || ''}`.trim();
            const teacherPhone = settings.teacher.phoneNumber || '';
            setCardFormData({name: teacherName, phoneNumber: teacherPhone});
        }
    }, [showAddCardModal, settings]);

    const handleAddCard = async (e) => {
        e.preventDefault();
        setAddCardLoading(true);
        try {
            const response = await addPaymentMethod(cardFormData.name, cardFormData.phoneNumber);
            if (response.success && response.url) {
                window.location.href = response.url;
            }
        } catch (err) {
            console.error('Failed to add card:', err);
        } finally {
            setAddCardLoading(false);
        }
    };

    const handleSelectPackage = (pkg) => {
        setSelectedPackage(pkg);
        setPurchaseResult(null);
        setError(null);
        setShowConfirm(true);
    };

    // Purchasing loader messages
    const LOADER_MESSAGES = [
        'מאמת פרטי תשלום...',
        'מעבד את הרכישה...',
        'מחייב את כרטיס האשראי...',
        'מוסיף קרדיטים לחשבון שלך...',
        'כמעט סיימנו...',
    ];
    const [loaderMessageIndex, setLoaderMessageIndex] = useState(0);
    const loaderIntervalRef = useRef(null);

    useEffect(() => {
        if (purchasing) {
            setLoaderMessageIndex(0);
            loaderIntervalRef.current = setInterval(() => {
                setLoaderMessageIndex(prev => prev < LOADER_MESSAGES.length - 1 ? prev + 1 : prev);
            }, 2500);
        } else {
            if (loaderIntervalRef.current) {
                clearInterval(loaderIntervalRef.current);
                loaderIntervalRef.current = null;
            }
        }
        return () => {
            if (loaderIntervalRef.current) clearInterval(loaderIntervalRef.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [purchasing]);

    const handleConfirmPurchase = async () => {
        if (purchasing || !selectedCardId) return;
        setPurchasing(true);
        setError(null);
        setShowConfirm(false);

        try {
            const result = await purchaseSmsCredits(selectedPackage.id, selectedCardId);
            if (result.success) {
                setPurchaseResult(result);
                setBalance({
                    success: true,
                    freeCredits: result.freeCredits,
                    purchasedCredits: result.purchasedCredits,
                    totalCredits: result.totalCredits,
                });
                setSelectedPackage(null);
            } else {
                setError(result.error || 'הרכישה נכשלה');
            }
        } catch (err) {
            setError('שגיאה בביצוע הרכישה');
        } finally {
            setPurchasing(false);
        }
    };

    const handleCancelConfirm = () => {
        setShowConfirm(false);
    };

    if (loadingBalance || loadingCards) {
        return <div className="text-center py-5">טוען...</div>;
    }

    return (
        <>
            {/* Fullscreen purchasing loader */}
            {purchasing && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.97)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '24px',
                }}>
                    <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="fw-bold text-dark mb-0">מבצע רכישה</h5>
                    <p className="text-muted mb-0" style={{transition: 'opacity 0.3s', fontSize: '1.05rem'}}>
                        {LOADER_MESSAGES[loaderMessageIndex]}
                    </p>
                    <p className="text-muted small mt-3">אנא אל תסגור את הדף</p>
                </div>
            )}

            <div className="col-12">
                {/* Balance Summary - compact */}
                <div className="d-flex align-items-center gap-3 mb-4 text-secondary-light text-sm">
                    <Icon icon="ph:wallet" className="icon"/>
                    <span>יתרה נוכחית: <strong className="text-dark">{balance?.totalCredits ?? 0}</strong> קרדיטים ({balance?.freeCredits ?? 0} חינם + {balance?.purchasedCredits ?? 0} שנרכשו)</span>
                </div>

                {/* Success Message */}
                {purchaseResult && (
                    <div className="alert alert-success d-flex align-items-center gap-2 mb-4" role="alert">
                        <Icon icon="ph:check-circle" className="icon" style={{fontSize: '1.5rem'}}/>
                        <div>
                            <strong>הרכישה בוצעה בהצלחה!</strong> נוספו {purchaseResult.creditsAdded} קרדיטים לחשבון שלך.
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="alert alert-danger d-flex align-items-center gap-2 mb-4" role="alert">
                        <Icon icon="ph:warning" className="icon" style={{fontSize: '1.5rem'}}/>
                        <div>{error}</div>
                    </div>
                )}

                {/* Packages */}
                <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
                    <h6 className="mb-2 fw-bold text-lg">בחר חבילת הודעות SMS</h6>
                </div>

                <div className="row g-4 mb-4">
                    {PACKAGES.map((pkg) => (
                        <div key={pkg.id} className="col-xl-3 col-sm-6">
                            <div
                                className={`card border-0 shadow-sm h-100 position-relative overflow-hidden`}
                                style={{
                                    borderRadius: '16px',
                                    cursor: 'pointer',
                                }}
                                onClick={() => handleSelectPackage(pkg)}
                            >
                                {pkg.popular && (
                                    <div className="position-absolute top-0 end-0">
                                        <span className="badge bg-warning text-dark px-3 py-2" style={{borderRadius: '0 16px 0 12px', fontSize: '0.75rem'}}>
                                            פופולרי
                                        </span>
                                    </div>
                                )}
                                <div className="card-body p-4 text-center d-flex flex-column">
                                    <div className="mb-3">
                                        <Icon icon="ph:envelope" className="icon text-primary" style={{fontSize: '2.5rem'}}/>
                                    </div>
                                    <h5 className="fw-bold mb-2">{pkg.label}</h5>
                                    <div className="mb-1">
                                        <span className="fw-bold text-primary" style={{fontSize: '1.8rem'}}>₪{pkg.price}</span>
                                        <span className="text-muted small d-block">לפני מע״מ</span>
                                    </div>
                                    <p className="text-muted small mb-1">₪{withTax(pkg.price)} כולל מע״מ</p>
                                    <p className="text-muted small mb-0">₪{pkg.pricePerSms} להודעה</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="d-flex align-items-start gap-2 text-secondary-light text-sm mb-4">
                    <Icon icon="ph:info" className="icon flex-shrink-0" style={{marginTop: '2px'}}/>
                    <span>קרדיטים שנרכשו לא מתאפסים בסוף החודש ונוספים ליתרת הקרדיטים החינמיים שלך (50 בחודש).</span>
                </div>
            </div>

            {/* Confirmation Modal — mobile-first full-screen sheet */}
            {showConfirm && selectedPackage && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div
                        className="modal fade show"
                        style={{display: 'block'}}
                        tabIndex="-1"
                        onClick={(e) => { if (e.target === e.currentTarget) handleCancelConfirm(); }}
                    >
                        <div className="modal-dialog modal-dialog-centered modal-fullscreen-sm-down" style={{margin: 'auto'}}>
                            <div className="modal-content" style={{borderRadius: 'clamp(0px, 2vw, 16px)'}}>
                                <div className="modal-header px-20 py-16">
                                    <h5 className="modal-title fw-bold">אישור רכישה</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={handleCancelConfirm}
                                        style={{width: '44px', height: '44px'}}
                                    ></button>
                                </div>
                                <div className="modal-body px-20 py-16" style={{overflowY: 'auto'}}>
                                    {/* Package summary */}
                                    <div className="card bg-light border-0 p-3 rounded-4 mb-4">
                                        <div className="text-center mb-3">
                                            <Icon icon="ph:envelope" className="icon text-primary" style={{fontSize: '2rem'}}/>
                                            <h6 className="fw-bold mt-2 mb-0">{selectedPackage.label}</h6>
                                        </div>
                                        <div className="d-flex justify-content-between py-2" style={{borderTop: '1px solid var(--bs-gray-300)'}}>
                                            <span>מחיר לפני מע״מ</span>
                                            <strong>₪{selectedPackage.price}</strong>
                                        </div>
                                        <div className="d-flex justify-content-between py-2" style={{borderTop: '1px solid var(--bs-gray-300)'}}>
                                            <span>מחיר כולל מע״מ</span>
                                            <strong className="text-primary">₪{withTax(selectedPackage.price)}</strong>
                                        </div>
                                        <div className="d-flex justify-content-between py-2" style={{borderTop: '1px solid var(--bs-gray-300)'}}>
                                            <span>מחיר להודעה</span>
                                            <strong>₪{selectedPackage.pricePerSms}</strong>
                                        </div>
                                    </div>

                                    {/* Card Selection */}
                                    <label className="form-label fw-bold mb-3">כרטיס אשראי לחיוב</label>
                                    {cards.length === 0 ? (
                                        <div className="text-center py-4">
                                            <p className="text-muted mb-3">לא נמצאו כרטיסי אשראי</p>
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary py-3 px-4"
                                                style={{minHeight: '48px'}}
                                                onClick={() => { setShowConfirm(false); setShowAddCardModal(true); }}
                                            >
                                                <Icon icon="ph:plus" className="icon inline-block mb-2 me-2"/>
                                                הוסף כרטיס אשראי
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-3">
                                            {cards.map(card => (
                                                <div
                                                    key={card.id}
                                                    className="d-flex align-items-center gap-3 p-3 rounded-4"
                                                    style={{
                                                        cursor: 'pointer',
                                                        minHeight: '56px',
                                                        border: selectedCardId === card.id ? '2px solid var(--bs-primary)' : '2px solid var(--bs-gray-200)',
                                                        background: selectedCardId === card.id ? 'var(--bs-primary-bg-subtle, #e7f1ff)' : 'transparent',
                                                        transition: 'all 0.2s',
                                                    }}
                                                    onClick={() => setSelectedCardId(card.id)}
                                                >
                                                    <div className={`d-flex align-items-center justify-content-center rounded-circle ${selectedCardId === card.id ? 'bg-primary text-white' : 'bg-light text-muted'}`}
                                                         style={{width: '40px', height: '40px', flexShrink: 0}}>
                                                        <Icon icon="ph:credit-card" className="icon"/>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="fw-semibold d-flex align-items-center gap-2">
                                                            {card.name}
                                                            {!!card.isDefault && (
                                                                <span className="badge bg-success px-2" style={{fontSize: '0.65rem'}}>ראשי</span>
                                                            )}
                                                        </div>
                                                        <div className="text-muted small" style={{letterSpacing: '1px'}}>
                                                            **** {card.lastDigits}
                                                        </div>
                                                    </div>
                                                    {selectedCardId === card.id && (
                                                        <Icon icon="ph:check-circle-fill" className="icon text-primary" style={{fontSize: '1.4rem'}}/>
                                                    )}
                                                </div>
                                            ))}
                                            <div
                                                className="d-flex align-items-center gap-3 p-3 rounded-4"
                                                style={{cursor: 'pointer', minHeight: '56px', border: '2px dashed var(--bs-gray-300)'}}
                                                onClick={() => { setShowConfirm(false); setShowAddCardModal(true); }}
                                            >
                                                <Icon icon="ph:plus-circle" className="icon text-primary" style={{fontSize: '1.3rem'}}/>
                                                <span className="text-primary fw-semibold">הוסף כרטיס חדש</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Sticky footer for mobile */}
                                <div className="modal-footer px-20 py-16 d-flex flex-column gap-2"
                                     style={{borderTop: '1px solid var(--bs-gray-200)', position: 'sticky', bottom: 0, background: 'white'}}>
                                    <button
                                        type="button"
                                        className="btn btn-primary w-100 py-3 fw-bold"
                                        style={{minHeight: '52px', fontSize: '1.05rem'}}
                                        onClick={handleConfirmPurchase}
                                        disabled={purchasing || !selectedCardId}
                                    >
                                        {purchasing ? (
                                            <>
                                                <Icon icon="ph:spinner" className="icon spin me-2"/>
                                                מבצע רכישה...
                                            </>
                                        ) : (
                                            `אשר רכישה - ₪${withTax(selectedPackage.price)}`
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-light w-100 py-2 text-muted"
                                        style={{minHeight: '44px'}}
                                        onClick={handleCancelConfirm}
                                    >
                                        ביטול
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Add Card Modal — mobile-first */}
            {showAddCardModal && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div
                        className="modal fade show"
                        style={{display: 'block'}}
                        tabIndex="-1"
                        onClick={(e) => { if (e.target === e.currentTarget) setShowAddCardModal(false); }}
                    >
                        <div className="modal-dialog modal-dialog-centered modal-fullscreen-sm-down" style={{margin: 'auto'}}>
                            <div className="modal-content" style={{borderRadius: 'clamp(0px, 2vw, 16px)'}}>
                                <div className="modal-header px-20 py-16">
                                    <h5 className="modal-title fw-bold">הוסף כרטיס אשראי</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowAddCardModal(false)}
                                        style={{width: '44px', height: '44px'}}
                                    ></button>
                                </div>
                                <form onSubmit={handleAddCard}>
                                    <div className="modal-body px-20 py-16">
                                        <div className="mb-4">
                                            <label htmlFor="cardName" className="form-label fw-semibold">שם מלא</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-lg"
                                                id="cardName"
                                                value={cardFormData.name}
                                                onChange={(e) => setCardFormData({...cardFormData, name: e.target.value})}
                                                required
                                                style={{minHeight: '48px'}}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label htmlFor="cardPhone" className="form-label fw-semibold">מספר טלפון</label>
                                            <input
                                                type="tel"
                                                className="form-control form-control-lg"
                                                id="cardPhone"
                                                value={cardFormData.phoneNumber}
                                                onChange={(e) => setCardFormData({...cardFormData, phoneNumber: e.target.value})}
                                                required
                                                style={{minHeight: '48px'}}
                                            />
                                        </div>
                                    </div>
                                    <div className="modal-footer px-20 py-16 d-flex flex-column gap-2"
                                         style={{borderTop: '1px solid var(--bs-gray-200)', position: 'sticky', bottom: 0, background: 'white'}}>
                                        <button
                                            type="submit"
                                            className="btn btn-primary w-100 py-3 fw-bold"
                                            style={{minHeight: '52px', fontSize: '1.05rem'}}
                                            disabled={addCardLoading}
                                        >
                                            {addCardLoading ? 'טוען...' : 'המשך'}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-light w-100 py-2 text-muted"
                                            style={{minHeight: '44px'}}
                                            onClick={() => setShowAddCardModal(false)}
                                        >
                                            ביטול
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default SmsCreditsPurchaseLayer;
